import {
  keepPreviousData,
  type QueryClient,
  type QueryState,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import type { DownloadTemplateSelection } from '@/entities/download-template';
import { searchKeys } from '@/entities/search';
import { API_TAGS, createLazyQuery, getApiErrorStatus } from '@/shared/api';
import { getShortName } from '@/shared/lib';
import { delay } from '@/shared/lib/utils';
import { downloadFileFromBlob } from '@/shared/lib/utils/file';
import { isTextConversation } from '../../lib/conversationType';
import { buildConversationAudioUrl } from './audioUrl';
import {
  applyConversationValues,
  createConversation,
  createConversationComment,
  createConversationPhrase,
  deleteConversationComment,
  deleteConversationPhrase,
  downloadConversationTranscript,
  fetchAllConversationsAttributes,
  fetchConversation,
  fetchConversationAttachmentFile,
  fetchConversationAttachmentSignedUrl,
  fetchConversationAudioBlob,
  fetchConversationAudioUrl,
  fetchConversationChecklist,
  fetchConversationCommentAuthors,
  fetchConversationComments,
  fetchConversationMessages,
  fetchConversationsAttributes,
  fetchConversationsClients,
  fetchConversationsEmployees,
  fetchConversationSignedUrl,
  fetchConversationTranscript,
  requestConversationRecordingFetch,
  runSpeechAnalytics,
  updateConversationComment,
  updateConversationPhrase,
  uploadConversationAudio,
} from './api';
import type {
  CommentCreateRequest,
  ConversationCreate,
  ConversationInfoResponse,
  ConversationSignedUrlResponse,
  PhraseCreateRequest,
  PhraseUpdateRequest,
} from './types';

/**
 * Query keys for conversations
 */
export const conversationsKeys = {
  all: [API_TAGS.DIALOGS] as const,
  lists: () => [...conversationsKeys.all, 'list'] as const,
  details: () => [...conversationsKeys.all, 'detail'] as const,
  detail: (id: string | number) => [...conversationsKeys.details(), id] as const,
  transcripts: () => [...conversationsKeys.all, 'transcript'] as const,
  transcript: (id: string | number, anonymized = true) => [...conversationsKeys.transcripts(), id, anonymized] as const,
  audioBlobs: () => [...conversationsKeys.all, 'audio-blob'] as const,
  audioBlob: (id: string | number) => [...conversationsKeys.audioBlobs(), id] as const,
  audioUrls: () => [...conversationsKeys.all, 'audio-url'] as const,
  audioUrl: (id: string | number) => [...conversationsKeys.audioUrls(), id] as const,
  checklists: () => [...conversationsKeys.all, 'checklist'] as const,
  checklist: (id: string | number, checklistId: string | number) =>
    [...conversationsKeys.checklists(), id, checklistId] as const,
  employees: () => [...conversationsKeys.all, 'employees'] as const,
  employeesByIds: (ids: string) => [...conversationsKeys.employees(), 'by-ids', ids] as const,
  clients: () => [...conversationsKeys.all, 'clients'] as const,
  clientsByIds: (ids: string) => [...conversationsKeys.clients(), 'by-ids', ids] as const,
  attributes: () => [...conversationsKeys.all, 'attributes'] as const,
  attributesList: (params?: Parameters<typeof fetchConversationsAttributes>[0]) =>
    [...conversationsKeys.attributes(), { ...params }] as const,
  attributesAll: (params?: Parameters<typeof fetchAllConversationsAttributes>[0]) =>
    [...conversationsKeys.attributes(), 'all', { ...params }] as const,
  comments: () => [...conversationsKeys.all, 'comments'] as const,
  commentsList: (id: string | number) => [...conversationsKeys.comments(), id] as const,
  commentAuthors: () => [...conversationsKeys.all, 'comment-authors'] as const,
  commentAuthorsList: (params?: Parameters<typeof fetchConversationCommentAuthors>[0]) =>
    [...conversationsKeys.commentAuthors(), { ...params }] as const,
  signedUrls: () => [...conversationsKeys.all, 'signed-url'] as const,
  signedUrl: (id: string | number) => [...conversationsKeys.signedUrls(), id] as const,
  attachmentSignedUrls: () => [...conversationsKeys.all, 'attachment-signed-url'] as const,
  attachmentSignedUrl: (attachmentId: string | number) =>
    [...conversationsKeys.attachmentSignedUrls(), attachmentId] as const,
  attachmentFiles: () => [...conversationsKeys.all, 'attachment-file'] as const,
  attachmentFile: (attachmentId: string | number) => [...conversationsKeys.attachmentFiles(), attachmentId] as const,
  messages: () => [...conversationsKeys.all, 'messages'] as const,
  messagesList: (id: string | number) => [...conversationsKeys.messages(), id] as const,
};

/**
 * Hook to fetch a single conversation by ID
 *
 * `retry: 0` + `retryOnMount: false`: при переключении макета на `DialogsPage`
 * активный макет перемонтируется. Без этих флагов запрос несуществующего диалога
 * (404) при каждом перемонтировании уходил бы в повторный fetch.
 *
 * @param id - Conversation ID
 */
export const useConversation = (id?: number | string) => {
  return useQuery({
    queryKey: conversationsKeys.detail(+id!),
    queryFn: ({ signal }) => fetchConversation(id!, { signal }),
    enabled: !!id,
    retry: 0,
    retryOnMount: false,
  });
};

/**
 * Hook to fetch conversation transcript per OpenAPI GET /conversation/{id}/transcript
 * Получение полного транскрипта разговора в виде упорядоченного списка фраз
 * с информацией о говорящих и временных метках.
 *
 * Анонимизированный и неанонимизированный виды кэшируются под разными ключами
 * (`anonymized` входит в ключ), поэтому переключение не затирает уже загруженный вид.
 * @param id - Conversation ID
 * @param anonymized - Вид текста реплик; по умолчанию true (анонимизированный)
 */
export const useConversationTranscript = (id?: string | number, anonymized = true) => {
  return useQuery({
    queryKey: conversationsKeys.transcript(+id!, anonymized),
    queryFn: ({ signal }) => fetchConversationTranscript(id!, anonymized, { signal }),
    enabled: !!id,
  });
};

/**
 * Переписка текстовой коммуникации ({@link fetchConversationMessages}).
 * @param enabled - запрашивать ли: переписка есть только у диалога вида `TEXT`
 */
export const useConversationMessages = (id?: string | number, enabled = true) => {
  return useQuery({
    queryKey: conversationsKeys.messagesList(+id!),
    queryFn: ({ signal }) => fetchConversationMessages(id!, { signal }),
    enabled: !!id && enabled,
  });
};

/** Диалог — текстовая коммуникация; пока карточка не получена — нет. */
export const useIsTextConversation = (id?: string | number): boolean => {
  const { data: conversation } = useConversation(id);
  return isTextConversation(conversation);
};

/**
 * Hook to fetch audio blob for a conversation
 * @param id - Conversation ID
 */
export const useConversationAudioBlob = (id?: string | number) => {
  return useQuery({
    queryKey: conversationsKeys.audioBlob(id!),
    queryFn: () => fetchConversationAudioBlob(id!),
    enabled: !!id,
  });
};

/**
 * Hook to fetch audio URL for a conversation
 * @param id - Conversation ID
 */
export const useConversationAudioUrl = (id?: string | number) => {
  return useQuery({
    queryKey: conversationsKeys.audioUrl(id!),
    queryFn: () => fetchConversationAudioUrl(id!),
    enabled: !!id,
  });
};

/**
 * Hook to upload conversation audio file
 */
export const useUploadConversationAudio = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: uploadConversationAudio,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: conversationsKeys.lists() });
    },
  });
};

/**
 * Hook to create a conversation
 * Новая запись меняет и списки диалогов, и выдачу поиска, из которой строится список диалогов, —
 * обесцениваются оба: иначе перечитанный список до истечения свежести выдачи показывал бы её без записи.
 */
export const useCreateConversation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: ConversationCreate) => createConversation(payload),
    onSuccess: (data) => {
      // Invalidate conversations list and search results
      queryClient.invalidateQueries({ queryKey: conversationsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: searchKeys.all });
      // Set the new conversation in cache
      queryClient.setQueryData(conversationsKeys.detail(data.id), data);
    },
  });
};

/**
 * Проставление отмеченным записям сотрудника, времени звонка и значений атрибутов.
 * Меняет строки списков, выдачу поиска и карточки записей — обесценивает все три.
 */
export const useApplyConversationValues = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: applyConversationValues,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: conversationsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: conversationsKeys.details() });
      queryClient.invalidateQueries({ queryKey: searchKeys.all });
    },
  });
};

/**
 * Hook to fetch conversation checklist
 */
export const useConversationChecklist = (id: string | number, checklistId?: string | number) => {
  return useQuery({
    queryKey: conversationsKeys.checklist(+id, +checklistId!),
    queryFn: () => fetchConversationChecklist(+id, +checklistId!),
    enabled: !!id && !!checklistId,
  });
};

/**
 * Hook to fetch employees of conversations
 */
export const useConversationsEmployees = (params?: Parameters<typeof fetchConversationsEmployees>[0]) => {
  return useQuery({
    queryKey: [...conversationsKeys.employees(), { ...params }] as const,
    queryFn: () => fetchConversationsEmployees(params),
  });
};

/**
 * Hook to fetch conversations' attributes
 */
export const useConversationsAttributes = (params?: Parameters<typeof fetchConversationsAttributes>[0]) => {
  return useQuery({
    queryKey: conversationsKeys.attributesList(params),
    queryFn: () => fetchConversationsAttributes(params),
  });
};

/**
 * Хук всего набора атрибутов для таблицы диалогов: набор доходит до последнего атрибута, сколько бы
 * окон выдачи он ни занимал. Нужен тем, кто предлагает выбрать атрибут из всех, — первое окно
 * выдачи за весь набор им выдавать нельзя.
 */
export const useAllConversationsAttributes = (params?: Parameters<typeof fetchAllConversationsAttributes>[0]) => {
  return useQuery({
    queryKey: conversationsKeys.attributesAll(params),
    queryFn: () => fetchAllConversationsAttributes(params),
  });
};

/** Имена участников диалогов, запрошенные по их идентификаторам */
export type ConversationPersonNames = {
  /** Идентификатор участника → ФИО; участника, которого нет в справочнике, в наборе нет */
  names: Record<number, string>;
  /** Имена ещё запрашиваются */
  isLoading: boolean;
};

/**
 * Набор идентификаторов как параметр точечной выборки: дубли не нужны, порядок не важен.
 * Нечисловые значения отбрасываются — идентификаторы участников числовые, а в набор они
 * попадают в том числе из сохранённых фильтров, где формат значения не гарантирован.
 */
export const toPersonIdsParam = (ids: Array<string | number>): string =>
  [...new Set(ids.map(Number))]
    .filter(Number.isFinite)
    .sort((a, b) => a - b)
    .join(',');

/**
 * Хук имён участников диалогов по их идентификаторам.
 *
 * Нужен там, где известны только идентификаторы участников (например, в фильтрах списка):
 * поиск по имени и первая страница справочника выбранных участников не гарантируют, поэтому
 * имена берутся точечной выборкой по идентификаторам.
 */
export const useConversationPersonNames = ({
  employeeIds,
  clientIds,
}: {
  employeeIds: number[];
  clientIds: number[];
}): ConversationPersonNames => {
  const employeeIdsParam = toPersonIdsParam(employeeIds);
  const clientIdsParam = toPersonIdsParam(clientIds);

  const employeesQuery = useQuery({
    queryKey: conversationsKeys.employeesByIds(employeeIdsParam),
    queryFn: () => fetchConversationsEmployees({ 'employee-id': employeeIdsParam }),
    enabled: Boolean(employeeIdsParam),
  });

  const clientsQuery = useQuery({
    queryKey: conversationsKeys.clientsByIds(clientIdsParam),
    queryFn: () => fetchConversationsClients({ 'client-id': clientIdsParam }),
    enabled: Boolean(clientIdsParam),
  });

  const names = useMemo(() => {
    const result: Record<number, string> = {};
    const persons = [...(employeesQuery.data?.employees ?? []), ...(clientsQuery.data?.clients ?? [])];
    persons.forEach((person) => {
      result[person.id] = getShortName(person.firstName, person.lastName, person.middleName);
    });
    return result;
  }, [employeesQuery.data, clientsQuery.data]);

  return { names, isLoading: employeesQuery.isLoading || clientsQuery.isLoading };
};

/**
 * Хук для ленивой загрузки сотрудников (ручной триггер).
 * Использует кэш React Query — повторный вызов с теми же params не делает HTTP-запрос.
 */
export const useLazyConversationsEmployees = createLazyQuery(
  (params: Parameters<typeof fetchConversationsEmployees>[0]) => [...conversationsKeys.employees(), { ...params }],
  fetchConversationsEmployees,
);

/**
 * Хук для ленивой загрузки клиентов (ручной триггер).
 * Использует кэш React Query — повторный вызов с теми же params не делает HTTP-запрос.
 */
export const useLazyConversationsClients = createLazyQuery(
  (params: Parameters<typeof fetchConversationsClients>[0]) => [...conversationsKeys.clients(), { ...params }],
  fetchConversationsClients,
);

/**
 * Hook to fetch comments for a conversation
 * @param id - Conversation ID
 */
export const useConversationComments = (id?: number | string) => {
  return useQuery({
    queryKey: conversationsKeys.commentsList(id!),
    queryFn: () => fetchConversationComments(id!),
    enabled: !!id,
  });
};

/**
 * Хук для получения авторов комментариев (наполнение выпадающего списка выбора автора
 * в поиске по комментариям). Загружается только когда список реально нужен — вызывать
 * из компонента, монтируемого лишь в активном режиме поиска по комментариям.
 */
export const useConversationCommentAuthors = (params?: Parameters<typeof fetchConversationCommentAuthors>[0]) => {
  return useQuery({
    queryKey: conversationsKeys.commentAuthorsList(params),
    queryFn: () => fetchConversationCommentAuthors(params),
  });
};

/**
 * Hook to fetch comments for a conversation with infinite scroll
 * @param id - Conversation ID
 * @param params - Query parameters (limit, offset)
 */
export const useInfiniteConversationComments = (id?: number | string, params?: { limit?: number; offset?: number }) => {
  const limit = params?.limit || 20;
  const initialPageParam = Math.floor((params?.offset || 0) / limit);

  const query = useInfiniteQuery({
    queryKey: [...conversationsKeys.commentsList(id!), { ...params }] as const,
    queryFn: ({ pageParam }) => fetchConversationComments(id!, { limit, offset: pageParam * limit }),
    initialPageParam,
    placeholderData: keepPreviousData,
    enabled: !!id,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      return lastPage.total <= limit * (lastPageParam + 1) ? undefined : lastPageParam + 1;
    },
  });

  const data = useMemo(
    () => ({
      total: query.data?.pages[0]?.total,
      comments: query.data?.pages.flatMap((p) => p.comments) || [],
    }),
    [query.data],
  );

  return {
    ...query,
    data,
  };
};

/**
 * Обесценивает списки диалогов после правки содержимого диалога — комментария или расшифровки.
 *
 * Содержимое видно не только в карточке открытого диалога: строка списка перечисляет комментарии,
 * а поиск по комментариям и по репликам отбирает по ним диалоги. Без этого возврат к списку
 * показывал бы выдачу, полученную до правки.
 */
const invalidateConversationLists = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: conversationsKeys.lists() });
  queryClient.invalidateQueries({ queryKey: searchKeys.all });
};

/**
 * Hook to create a comment for a conversation
 */
export const useCreateConversationComment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: conversationsKeys.comments(),
    mutationFn: ({ id, payload }: { id: number | string; payload: CommentCreateRequest }) =>
      createConversationComment(id, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: conversationsKeys.commentsList(variables.id) });
      invalidateConversationLists(queryClient);
    },
  });
};

/**
 * Hook to update a comment
 */
export const useUpdateConversationComment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: conversationsKeys.comments(),
    mutationFn: ({ id, payload }: { id: number | string; payload: CommentCreateRequest }) =>
      updateConversationComment(id, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: conversationsKeys.commentsList(data.conversationId) });
      invalidateConversationLists(queryClient);
    },
  });
};

/**
 * Hook to delete a comment
 * Удаление комментария. Только автор комментария может его удалить.
 * Требуется разрешение VIEW_CONVERSATION.
 */
export const useDeleteConversationComment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: conversationsKeys.comments(),
    mutationFn: (id: string | number): Promise<void> => deleteConversationComment(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: conversationsKeys.comments() });
      invalidateConversationLists(queryClient);
    },
  });
};

/**
 * Обесценивает всё, что показывает расшифровку диалога, после её правки: транскрипт в обоих видах
 * анонимизации, карточку диалога (признак наличия транскрипции), списки и поиск по репликам,
 * перечень реплик аудита аудио — правленый текст и время обновления видны и там.
 */
const invalidateConversationTranscript = (queryClient: QueryClient, conversationId: number | string) => {
  queryClient.invalidateQueries({ queryKey: conversationsKeys.transcripts() });
  queryClient.invalidateQueries({ queryKey: conversationsKeys.detail(+conversationId) });
  // Ключ перечня аудита аудио — корень его тега: сущность реплик сама зависит от этой и сюда не импортируется
  queryClient.invalidateQueries({ queryKey: [API_TAGS.PHRASES] });
  invalidateConversationLists(queryClient);
};

/**
 * Добавление записи расшифровки: реплики, перебивания или паузы, в том числе выносом фрагмента.
 */
export const useCreateConversationPhrase = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ conversationId, data }: { conversationId: number | string; data: PhraseCreateRequest }) =>
      createConversationPhrase(conversationId, data),
    onSuccess: (_phrase, { conversationId }) => invalidateConversationTranscript(queryClient, conversationId),
  });
};

/**
 * Правка записи расшифровки — уходят только изменённые поля.
 */
export const useUpdateConversationPhrase = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      conversationId,
      phraseId,
      data,
    }: {
      conversationId: number | string;
      phraseId: number;
      data: PhraseUpdateRequest;
    }) => updateConversationPhrase(conversationId, phraseId, data),
    onSuccess: (_phrase, { conversationId }) => invalidateConversationTranscript(queryClient, conversationId),
  });
};

/**
 * Удаление записи расшифровки вместе с перебиваниями и отметками стоп-слов удаляемой реплики.
 */
export const useDeleteConversationPhrase = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ conversationId, phraseId }: { conversationId: number | string; phraseId: number }) =>
      deleteConversationPhrase(conversationId, phraseId),
    onSuccess: (_data, { conversationId }) => invalidateConversationTranscript(queryClient, conversationId),
  });
};

/**
 * Hook to run speech analytics for a conversation
 */
export const useRunSpeechAnalytics = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number | string) => runSpeechAnalytics(id),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: conversationsKeys.detail(variables) });
    },
  });
};

/**
 * Хук для ленивого получения подписанной ссылки на вложение (ручной триггер).
 * Использует кэш React Query — повторный вызов с тем же attachmentId не делает HTTP-запрос.
 * Применимо только для вложений с isExternalUrl=false.
 */
export const useLazyConversationAttachmentSignedUrl = createLazyQuery(
  (attachmentId: number) => conversationsKeys.attachmentSignedUrl(attachmentId),
  fetchConversationAttachmentSignedUrl,
);

/**
 * Ленивое получение файла вложения из локального хранилища ({@link fetchConversationAttachmentFile}).
 * Файл вложения не меняется, поэтому полученный повторно не перезапрашивается; отказ не повторяется.
 */
export const useLazyConversationAttachmentFile = createLazyQuery(
  (attachmentId: number) => conversationsKeys.attachmentFile(attachmentId),
  fetchConversationAttachmentFile,
  { staleTime: Number.POSITIVE_INFINITY, retry: false },
);

/**
 * Хук для скачивания транскрипции диалога по выбранному шаблону.
 * Скачивает файл и инициирует загрузку через blob URL; расширение имени берётся из выбранного
 * формата — заголовок ответа кросс-доменному запросу браузером не открывается.
 */
export const useDownloadConversationTranscript = () => {
  return useMutation({
    mutationFn: ({ id, templateId, format }: { id: number | string } & DownloadTemplateSelection) =>
      downloadConversationTranscript(id, templateId, format),
    onSuccess: (blob, { id, format }) => {
      downloadFileFromBlob(blob, `transcript_${id}.${format}`);
    },
  });
};

/**
 * Сколько всего ждать запись, которую подгружает интеграция-источник (мс).
 * Ожидание держит индикатор — в области записи карточки или на кнопке прослушивания, — поэтому
 * у него есть предел: не дождавшись, честнее сказать о неудаче, чем ждать молча. Получение
 * у источника может идти и дольше, но по исчерпании ожидания его повтор — решение пользователя.
 */
const RECORDING_WAIT_TIMEOUT_MS = 3 * 60_000;

/**
 * С какой паузы начинается опрос ссылки, пока запись ожидается от источника (мс).
 */
const RECORDING_WAIT_INITIAL_INTERVAL_MS = 2000;

/**
 * До какой паузы между запросами ссылки доходит опрос ожидания (мс).
 * Пауза удваивается от запроса к запросу: каждый запрос ссылки заново проверяет запись
 * в хранилище и заказ у источника, поэтому ровный частый опрос нагружал бы сервер, ничего
 * не ускоряя. Растущая пауза оставляет частыми первые секунды — те, за которые запись
 * появляется чаще всего.
 */
const RECORDING_WAIT_MAX_INTERVAL_MS = 10_000;

/**
 * Код ответа, которым диалог без записи сообщает, что взять её неоткуда.
 * Прочие коды об отсутствии записи не говорят — это сбой запроса.
 */
const RECORDING_ABSENT_STATUS = 400;

/**
 * Чем закончился запрос записи диалога:
 * - ready — ссылка на запись получена;
 * - absent — записи нет и получить её неоткуда;
 * - failed — запись получить не удалось: источник её не отдал, ожидание не дало результата
 *   либо запрос не прошёл;
 * - cancelled — ожидание прекращено вызывающей стороной.
 */
export type RecordingUrlResult =
  | { status: 'ready'; audioUrl: string }
  | { status: 'absent' }
  | { status: 'failed' }
  | { status: 'cancelled' };

/**
 * Пауза до следующего запроса ссылки в ожидании записи, когда в этом ожидании завершилось
 * `settledFetches` запросов; null — срок ожидания вышел. Единый график для обоих ожиданий —
 * по действию пользователя и в карточке диалога: паузы растут вдвое от
 * {@link RECORDING_WAIT_INITIAL_INTERVAL_MS} до {@link RECORDING_WAIT_MAX_INTERVAL_MS} и в сумме
 * не выходят за {@link RECORDING_WAIT_TIMEOUT_MS}. Срок считается по числу запросов, а не по часам, —
 * поэтому одинаково читается и при опросе, и при показе.
 */
const getRecordingWaitPause = (settledFetches: number): number | null => {
  let waitedMs = 0;
  let pauseMs = RECORDING_WAIT_INITIAL_INTERVAL_MS;
  for (let fetchNumber = 1; fetchNumber < settledFetches; fetchNumber++) {
    waitedMs += pauseMs;
    pauseMs = Math.min(pauseMs * 2, RECORDING_WAIT_MAX_INTERVAL_MS);
  }

  const remainingMs = RECORDING_WAIT_TIMEOUT_MS - waitedMs;
  return remainingMs > 0 ? Math.min(pauseMs, remainingMs) : null;
};

/**
 * Запрос адреса записи диалога по действию пользователя — с ожиданием записи, которую
 * подгружает интеграция-источник, и с возможностью это ожидание прекратить.
 *
 * Нужен там, где запись слушают из списка: у строки списка нет карточки диалога, в которой
 * ожидание жило бы само, поэтому ждёт сам запрос. Ответ без ссылки означает, что получение
 * записи заказано, — повторяем запрос, пока запись не появится или не выйдет срок ожидания.
 */
export const useConversationRecordingUrl = () => {
  const queryClient = useQueryClient();

  const requestRecordingUrl = useCallback(
    async (id: number | string, signal?: AbortSignal): Promise<RecordingUrlResult> => {
      // Паузы — по общему с карточкой графику, а срок по часам — страховка от долгих запросов:
      // перебрать его — значит держать кнопку дольше обещанного
      const deadline = Date.now() + RECORDING_WAIT_TIMEOUT_MS;

      for (let settledFetches = 1; ; settledFetches++) {
        if (signal?.aborted) return { status: 'cancelled' };

        let data: ConversationSignedUrlResponse;
        try {
          // staleTime: 0 — ответ об ожидании устаревает сразу, иначе опрос читал бы кэш
          data = await queryClient.fetchQuery({
            queryKey: conversationsKeys.signedUrl(id),
            queryFn: () => fetchConversationSignedUrl(id),
            staleTime: 0,
            retry: false,
          });
        } catch (error) {
          // Об отсутствии записи говорит только свой код ответа. Сетевой сбой, ошибка сервера
          // и истёкшая сессия записи не отменяют — выдавать их за «записи нет» нельзя
          return { status: getApiErrorStatus(error) === RECORDING_ABSENT_STATUS ? 'absent' : 'failed' };
        }

        if (signal?.aborted) return { status: 'cancelled' };
        if (data.url) return { status: 'ready', audioUrl: buildConversationAudioUrl(data.url) };
        // Источник запись уже не отдал, а сам сервер её больше не заказывает — ждать нечего
        if (data.recordingState === 'FAILED') return { status: 'failed' };
        // Ссылки нет и запись ниоткуда не ожидается — слушать нечего
        if (data.recordingState !== 'PENDING') return { status: 'absent' };

        const pauseMs = getRecordingWaitPause(settledFetches);
        const remainingMs = deadline - Date.now();
        if (pauseMs === null || remainingMs <= 0) break;

        await delay(Math.min(pauseMs, remainingMs), signal);
      }

      return signal?.aborted ? { status: 'cancelled' } : { status: 'failed' };
    },
    [queryClient],
  );

  return { requestRecordingUrl };
};

/**
 * Сколько запросов ссылки на запись завершилось — удачно или отказом. Счёт ведёт кэш запроса,
 * поэтому перезапрос ссылки его не сбрасывает.
 */
const countSignedUrlFetches = (state?: Pick<QueryState, 'dataUpdateCount' | 'errorUpdateCount'>) =>
  (state?.dataUpdateCount ?? 0) + (state?.errorUpdateCount ?? 0);

/**
 * Может ли отказ в ссылке пройти сам: сетевой сбой и ошибка сервера — да, а отказ по запросу
 * (записи нет, диалог недоступен, сессия истекла) повтор не изменит.
 */
const isSignedUrlFailureTransient = (error: unknown) => {
  const status = getApiErrorStatus(error);
  return status === undefined || status >= 500;
};

/**
 * Пауза до следующего запроса ссылки, пока карточка ждёт запись; null — ждать больше нечего.
 * Ждать стоит, только пока запись ожидается от источника или отказ в ссылке может пройти,
 * и не дольше срока ожидания. Одно правило и для опроса, и для признака конца ожидания: иначе
 * опрос мог бы закончиться, а область записи — остаться с индикатором.
 */
const getSignedUrlWaitPause = (
  state: QueryState<ConversationSignedUrlResponse>,
  settledFetchesBeforeWait: number,
): number | null => {
  // По последнему завершившемуся запросу: идущий перезапрос признак сбоя не снимает
  const isLastFetchFailed = state.errorUpdatedAt > state.dataUpdatedAt;
  const isRecordingAwaited = isLastFetchFailed
    ? isSignedUrlFailureTransient(state.error)
    : !state.data || (!state.data.url && state.data.recordingState === 'PENDING');
  if (!isRecordingAwaited) return null;

  return getRecordingWaitPause(countSignedUrlFetches(state) - settledFetchesBeforeWait);
};

/**
 * Хук подписанной ссылки на аудиофайл для AudioPlayer с ожиданием записи в карточке диалога.
 * Пока запись ожидается от источника (или отказ в ссылке может пройти), опрашивает
 * GET /conversation/{id}/signed-url с растущей паузой в пределах срока ожидания.
 * @param id - Conversation ID
 * @param isAwaitAllowed - Ждать ли запись: у ненайденного диалога ждать нечего
 * @returns
 * - `audioUrl` — полный адрес записи; undefined, пока ссылки нет;
 * - `recordingState` и `recordingStateUpdatedAt` — состояние записи из последнего ответа
 *   на запрос ссылки и момент этого ответа;
 * - `isRecordingAbsent` — последний запрос отклонён: записи нет и взять её неоткуда;
 * - `isWaitOver` — ссылки нет и ждать её больше нечего;
 * - `restartWait` — начать ожидание заново, с полным сроком.
 */
export const useConversationAudioUrlWithSignedParams = (id?: string | number, isAwaitAllowed = true) => {
  const queryClient = useQueryClient();
  const getSignedUrlState = useCallback(
    () => (id ? queryClient.getQueryState<ConversationSignedUrlResponse>(conversationsKeys.signedUrl(id)) : undefined),
    [id, queryClient],
  );

  // Ожидание начинается с открытия карточки: запросы ссылки, завершившиеся до него, в счёт срока
  // не входят — иначе снова открытая карточка не дождалась бы запись, пришедшую за это время
  const [waitStart, setWaitStart] = useState(() => ({
    id,
    settledFetches: countSignedUrlFetches(getSignedUrlState()),
  }));
  if (waitStart.id !== id) {
    setWaitStart({ id, settledFetches: countSignedUrlFetches(getSignedUrlState()) });
  }

  const restartWait = useCallback(() => {
    setWaitStart({ id, settledFetches: countSignedUrlFetches(getSignedUrlState()) });
  }, [id, getSignedUrlState]);

  const {
    data: signedUrlData,
    dataUpdatedAt,
    errorUpdatedAt,
    error,
    isFetching,
  } = useQuery({
    queryKey: conversationsKeys.signedUrl(id!),
    queryFn: () => fetchConversationSignedUrl(id!),
    enabled: !!id,
    retry: false,
    // Опрос ведёт сам запрос: у всех мест карточки, ждущих запись, он один, и одновременные
    // перезапросы в нём не множатся. Срок считается по кэшу запроса, а не по жизни интервала,
    // поэтому перезапрос ожидание заново не начинает.
    refetchInterval: (query) => {
      if (!isAwaitAllowed) return false;
      return getSignedUrlWaitPause(query.state, waitStart.settledFetches) ?? false;
    },
    refetchIntervalInBackground: true,
  });

  const audioUrl = useMemo(() => {
    if (!signedUrlData?.url || !id) {
      return undefined;
    }

    return buildConversationAudioUrl(signedUrlData.url);
  }, [signedUrlData?.url, id]);

  // Отказ — по последнему завершившемуся запросу: перезапрос ссылки признак не снимает,
  // иначе область записи перестраивалась бы на каждой попытке
  const isRecordingAbsent = errorUpdatedAt > dataUpdatedAt && getApiErrorStatus(error) === RECORDING_ABSENT_STATUS;

  // Пока запрос ссылки идёт, ответа ещё ждут; дальше — то же правило, по которому идёт опрос
  const signedUrlState = getSignedUrlState();
  const isWaitOver =
    !audioUrl &&
    !isFetching &&
    !!signedUrlState &&
    getSignedUrlWaitPause(signedUrlState, waitStart.settledFetches) === null;

  return {
    audioUrl,
    recordingState: signedUrlData?.recordingState,
    recordingStateUpdatedAt: dataUpdatedAt,
    isRecordingAbsent,
    isWaitOver,
    restartWait,
  };
};

/**
 * Повторный запрос записи диалога у интеграции-источника по действию пользователя.
 * Ответ сразу ложится в карточку диалога, а ожидаемая запись — и в ссылку: область записи
 * переходит к ожиданию, не показывая прежнюю неудачу. Ссылка перечитывается при любом исходе —
 * по ней видно и готовую запись, и то, что получить её стало неоткуда.
 */
const useRequestConversationRecordingFetch = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: requestConversationRecordingFetch,
    onSuccess: ({ recordingState }, id) => {
      queryClient.setQueryData<ConversationInfoResponse>(
        conversationsKeys.detail(+id),
        (conversation) => conversation && { ...conversation, recordingState },
      );
      if (recordingState === 'PENDING') {
        queryClient.setQueryData<ConversationSignedUrlResponse>(conversationsKeys.signedUrl(id), {
          url: null,
          recordingState,
        });
      }
    },
    onSettled: (_data, _error, id) => {
      // Без ожидания перечитывания: исход запроса не должен ждать ответа о ссылке
      void queryClient.invalidateQueries({ queryKey: conversationsKeys.signedUrl(id) });
    },
  });
};

/**
 * Состояние отображения аудио-области карточки диалога:
 * - loading — признак состояния записи ещё неизвестен (карточка диалога грузится):
 *   держим стабильное зарезервированное место БЕЗ индикатора, чтобы не было дёрганья;
 * - unavailable — записи нет, показывать «запись недоступна»;
 * - failed — получить запись у источника не удалось: «запись недоступна» с повторным запросом;
 * - pending — запись ожидается/подгружается, показывать индикатор загрузки;
 * - ready — ссылка на запись готова, показывать плеер.
 */
export type DialogAudioStatus = 'loading' | 'unavailable' | 'failed' | 'pending' | 'ready';

/**
 * Объединяет признак состояния записи из карточки диалога (recordingState) с фактической
 * готовностью подписанной ссылки на аудио. Нужен, чтобы аудио-область заранее отличала
 * «ждать запись» (индикатор) от «записи нет» (сразу «недоступна») и не дёргалась при
 * позднем появлении плеера.
 *
 * Индикатор (pending) показывается СТРОГО пока ожидание идёт: запись ожидается от источника
 * или уже готова, но ссылка ещё догружается. Неудача у источника, отказ в ссылке, который ждать
 * бессмысленно, и исчерпанный срок ожидания индикатор снимают. Пока признак состояния неизвестен
 * (карточка грузится) — состояние loading: зарезервированное место без индикатора, чтобы
 * не возникало скачка индикатор → «недоступна».
 *
 * Карточка диалога не получена — диалог не найден (удалён или недоступен): область сразу «недоступна»,
 * и запись такого диалога не ожидается.
 *
 * @returns `status`, `audioUrl` и `retryRecordingFetch` — повторный запрос записи у источника,
 * начинающий ожидание заново.
 */
export const useDialogAudioState = (id?: string | number) => {
  const { data: conversation, dataUpdatedAt, errorUpdatedAt } = useConversation(id);
  // Последний завершившийся запрос карточки отклонён. Признак держится и пока карточка
  // перезапрашивается, поэтому вид области записи от идущего запроса не зависит.
  const isConversationMissing = errorUpdatedAt > dataUpdatedAt;
  const isText = isTextConversation(conversation);
  // Ссылку на запись запрашиваем и запись ждём только у полученной карточки звонка: у текстовой
  // коммуникации записи нет, а вид диалога известен лишь из карточки
  const isRecordingExpected = !!conversation && !isConversationMissing && !isText;
  const {
    audioUrl,
    recordingState: linkRecordingState,
    recordingStateUpdatedAt,
    isRecordingAbsent,
    isWaitOver,
    restartWait,
  } = useConversationAudioUrlWithSignedParams(isRecordingExpected ? id : undefined, isRecordingExpected);
  const { mutate: requestRecordingFetch, isPending: isRetrying } = useRequestConversationRecordingFetch();

  // Из двух ответов о состоянии записи действует более свежий: ссылку опрашивают, пока запись
  // ждут, а карточку обновляет повторный запрос записи
  const recordingState =
    linkRecordingState && recordingStateUpdatedAt >= dataUpdatedAt ? linkRecordingState : conversation?.recordingState;

  const status = useMemo<DialogAudioStatus>(() => {
    // Диалог не найден или это текстовая коммуникация — записи у него нет.
    if (isConversationMissing || isText) return 'unavailable';
    // Ссылка готова — показываем плеер независимо от признака состояния.
    if (audioUrl) return 'ready';
    // Повторный запрос записи начинает ожидание заново — индикатор сразу, не дожидаясь ответа.
    if (isRetrying) return 'pending';
    if (isRecordingAbsent || recordingState === 'ABSENT') return 'unavailable';
    if (recordingState === 'FAILED') return 'failed';
    // Ожидание кончилось без ссылки: запись, которую ждали от источника, получить не удалось,
    // а готовой записи ссылку так и не выдали.
    if (isWaitOver) return recordingState === 'PENDING' ? 'failed' : 'unavailable';
    // Запись ожидается (внешняя привязка + активный источник) либо уже готова, но ссылка ещё
    // догружается — индикатор в зарезервированном месте, плеер появится без скачка разметки.
    if (recordingState === 'PENDING' || recordingState === 'READY') return 'pending';
    // Признак состояния ещё неизвестен (карточка грузится): держим зарезервированное место
    // без индикатора; индикатор «просто потому что ждём» не показываем.
    return 'loading';
  }, [audioUrl, recordingState, isRecordingAbsent, isWaitOver, isRetrying, isConversationMissing, isText]);

  const retryRecordingFetch = useCallback(() => {
    if (!id) return;
    // Новое ожидание отсчитывается от ответа на повторный запрос, а не от прежнего, уже исчерпанного
    requestRecordingFetch(id, { onSettled: restartWait });
  }, [id, requestRecordingFetch, restartWait]);

  return { status, audioUrl, retryRecordingFetch };
};
